import { useEffect, useState, useCallback } from "react";
import api, { extractError, API } from "@/lib/api";
import { SectionHeader } from "@/components/SectionHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Check, X, FileDown } from "lucide-react";
import { toast } from "sonner";
import { formatCompactINR, formatDate, formatNumber } from "@/lib/format";
import { StatusPill } from "@/components/StatusPill";

export default function ApprovalsPage() {
  const [rows, setRows] = useState([]);
  const [reject, setReject] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/submissions", { params: { status: "pending" } });
    setRows(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    try {
      await api.post(`/submissions/${r.id}/approve`);
      toast.success("Approved");
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const doReject = async () => {
    if (!reason.trim()) return toast.error("Rejection reason required");
    try {
      await api.post(`/submissions/${reject.id}/reject`, { reason });
      toast.success("Rejected");
      setReject(null);
      setReason("");
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  return (
    <div data-testid="approvals-page">
      <SectionHeader title="Approvals" description="Review pending submissions and approve or reject." />
      <Card className="rounded-xl border border-border bg-card p-4 shadow-sm">
        <div className="overflow-x-auto rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead className="label-caps">Date</TableHead>
                <TableHead className="label-caps">Executive</TableHead>
                <TableHead className="label-caps">Client</TableHead>
                <TableHead className="label-caps">Type</TableHead>
                <TableHead className="label-caps text-right">Deposit</TableHead>
                <TableHead className="label-caps text-right">Accounts</TableHead>
                <TableHead className="label-caps">Remark</TableHead>
                <TableHead className="label-caps">File</TableHead>
                <TableHead className="label-caps text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`approval-row-${r.id}`} className="hover:bg-muted/40">
                  <TableCell className="text-sm tabular-nums">{formatDate(r.date)}</TableCell>
                  <TableCell className="text-sm">{r.user_name || "—"}</TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{r.client_name}</div>
                    <div className="text-xs text-muted-foreground tabular-nums">{r.client_code}</div>
                  </TableCell>
                  <TableCell><StatusPill status={r.client_type}>{r.client_type}</StatusPill></TableCell>
                  <TableCell className="text-right tabular-nums">{formatCompactINR(r.deposit_amount)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatNumber(r.accounts_opened)}</TableCell>
                  <TableCell className="max-w-xs truncate text-xs text-muted-foreground">{r.remark || "—"}</TableCell>
                  <TableCell>
                    {r.attachment_path ? (
                      <a
                        href={`${API}/submissions/attachment/${r.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        <FileDown className="h-3.5 w-3.5" /> View
                      </a>
                    ) : (<span className="text-xs text-muted-foreground">—</span>)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <Button
                        size="sm"
                        onClick={() => approve(r)}
                        data-testid={`approve-btn-${r.id}`}
                        className="bg-[hsl(var(--success))] text-white hover:bg-[hsl(var(--success))]/90"
                      >
                        <Check className="mr-1 h-3.5 w-3.5" /> Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setReject(r)}
                        data-testid={`reject-btn-${r.id}`}
                        className="border-[hsl(var(--danger))]/40 text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger))]/10"
                      >
                        <X className="mr-1 h-3.5 w-3.5" /> Reject
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="py-16 text-center text-sm text-muted-foreground">
                    All caught up — no pending approvals.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!reject} onOpenChange={(o) => !o && setReject(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader><DialogTitle>Reject submission</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label>Reason</Label>
            <Textarea data-testid="reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="e.g. Missing KYC document" />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setReject(null)}>Cancel</Button>
            <Button
              onClick={doReject}
              data-testid="reject-submit"
              className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90"
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
